import type { CreatorStatusTab } from "../collaboration-table-columns";
import type { CollaborationKind } from "./collaboration-insights-panel-view";
import { GroupCollaborationInsightsPanel } from "./group-collaboration-insights-panel";
import { OpenCollaborationInsightsPanel } from "./open-collaboration-insights-panel";
import { TargetCollaborationInsightsPanel } from "./target-collaboration-insights-panel";

type Props = {
  /** Exactly one of collaborationId / groupId is expected. */
  collaborationId?: string;
  /** Renders the panel aggregated across every collaboration in this group. */
  groupId?: string;
  showRecommendations?: boolean;
  /** Which collaboration table the insights queries should target. Defaults to "target". Ignored when groupId is set. */
  collaborationType?: CollaborationKind;
  /**
   * Called when the user clicks a funnel bar, performance segment, or the
   * Total GMV summary tile. The panel translates the clicked element into the
   * matching creators-table status filter. Passing `null` clears the filter.
   */
  onFilterChange?: (status: CreatorStatusTab) => void;
  /**
   * Called when the user clicks "Filter Creators" on a Creator Breakdown bucket card.
   * The value is the bucket key ("3plus" | "1to2" | "first") or null to clear.
   */
  onBreakdownFilterChange?: (bucket: string | null) => void;
};

export function CollaborationInsightsPanel({
  collaborationId,
  groupId,
  showRecommendations,
  collaborationType = "target",
  onFilterChange,
  onBreakdownFilterChange,
}: Props) {
  if (groupId) {
    return (
      <GroupCollaborationInsightsPanel
        groupId={groupId}
        showRecommendations={showRecommendations}
        onFilterChange={onFilterChange}
        onBreakdownFilterChange={onBreakdownFilterChange}
      />
    );
  }

  if (collaborationType === "open") {
    return (
      <OpenCollaborationInsightsPanel
        collaborationId={collaborationId}
        showRecommendations={showRecommendations}
        onFilterChange={onFilterChange}
        onBreakdownFilterChange={onBreakdownFilterChange}
      />
    );
  }

  return (
    <TargetCollaborationInsightsPanel
      collaborationId={collaborationId}
      showRecommendations={showRecommendations}
      onFilterChange={onFilterChange}
      onBreakdownFilterChange={onBreakdownFilterChange}
    />
  );
}
